import { Location, NgClass, TitleCasePipe } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';

import {
    CommonService,
    NLRegulatoryHelperService,
    OfferContentConfiguration,
    TrackingModelService,
    UserComplianceMessageComponent,
} from '@frontend/promo-homewidget';
import { DSComponents, DesignSystemHelperService, PromoDetailPageConfiguration } from '@frontend/promo-utils';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { DeviceService, ViewTemplateForClient } from '@frontend/vanilla/core';
import { HeaderBarComponent } from '@frontend/vanilla/features/header-bar';
import { LetDirective } from '@ngrx/component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CCBEventService } from '../../../../common/ccb-event-service/ccb-event.service';
import { OnClickWTDirective } from '../../../../common/web-tracking/onclick-web-tracking.directive';
import { WebAnalyticModel } from '../../../../common/web-tracking/web-analytic.model';
import { SeoPageContentComponent } from '../../../../seo-page/seo-page-content/seo-page-content.component';
import { CommonEventService } from '../events/common-event.service';
import { ScrollingEventService } from '../events/scrolling-event.service';
import { CommonEnum } from '../models/promo-information';

@Component({
    selector: 'tp-base-layout',
    templateUrl: './base-layout.component.html',
    imports: [
        UserComplianceMessageComponent,
        LetDirective,
        NgClass,
        HeaderBarComponent,
        OnClickWTDirective,
        RouterOutlet,
        SeoPageContentComponent,
        TitleCasePipe,
        DsTabsModule,
    ],
})
export class BaseLayoutComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
    public readonly leaderboard: string = 'leaderboard';
    public readonly ranking: string = 'ranking';
    @Input() tabs: string[];
    @Input() clientContent: ViewTemplateForClient;
    @Input() title: string;

    isMobile: boolean = false;
    topOffSet: number;
    isScrolledTop: boolean = false;
    hideNavBar: boolean = false;
    webAnalyticModel: WebAnalyticModel = new WebAnalyticModel();
    currentTab: string;
    isOptinPresent: boolean;
    isDesktop: boolean = false;
    private readonly destroy$ = new Subject<void>();

    @ViewChild('baseLayout') baseLayoutElement: ElementRef;
    productName: any;
    designSystem: DSComponents;
    tabIndex: number = 0;

    constructor(
        public device: DeviceService,
        public offerContentConfiguration: OfferContentConfiguration,
        private scrollingEventService: ScrollingEventService,
        private commonEventService: CommonEventService,
        private trackingService: TrackingModelService,
        private commonService: CommonService,
        private ccbEventService: CCBEventService,
        public nLRegulatoryHelperService: NLRegulatoryHelperService,
        public promoDetailPageConfiguration: PromoDetailPageConfiguration,
        public location: Location,
        private designSystemHelperService: DesignSystemHelperService,
        public router: Router,
        private route: ActivatedRoute,
    ) {}

    ngOnInit() {
        const url = this.location.path().split('/');
        this.productName = url[3];
        this.isDesktop = !this.device?.isMobile;
        this.designSystem = this.designSystemHelperService.getDsComponentsList();
        if (!this.isDesktop) {
            this.scrollingEventService
                .getStickyTopStatus()
                .pipe(takeUntil(this.destroy$))
                .subscribe((isStickyTop) => {
                    this.hideNavBar = !this.isDesktop && this.currentTab == CommonEnum.Ranking && isStickyTop;
                });
        }

        this.commonEventService
            .getWAModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe((wa: WebAnalyticModel) => {
                this.webAnalyticModel = wa;
            });
        //hiding global search icon
        dispatchEvent(new CustomEvent('gs:hideFloatingEntry'));
        //css fix
        this.checkIfOptinLoaded();
    }

    setCurrentTab() {
        if (this.tabs) {
            if (this.route?.firstChild) this.currentTab = this.tabs.find((tab) => tab == this.route?.firstChild?.routeConfig?.path) ?? '';
            if (!this.currentTab || this.currentTab == '') {
                this.currentTab = this.tabs[0];
            }
            this.router.navigate([`./${this.currentTab == this.leaderboard ? this.ranking : this.currentTab}`], {
                relativeTo: this.route,
                replaceUrl: true,
                queryParamsHandling: 'preserve',
            });
        }
    }

    ngOnChanges() {
        if (this.tabs) {
            this.setCurrentTab();
        }
    }

    ngAfterViewChecked() {
        if (this.tabs) {
            this.tabIndex = this.tabs.indexOf(this.currentTab);
        }
    }

    checkIfOptinLoaded() {
        this.commonEventService.isOptinLoaded.pipe(takeUntil(this.destroy$)).subscribe((loaded: boolean) => (this.isOptinPresent = loaded));
    }

    changeCurrentTab(event: any) {
        if (event) {
            this.tabIndex = Number(event.name);
            this.currentTab = this.tabs[this.tabIndex];
            this.router.navigate([`./${this.currentTab == this.leaderboard ? this.ranking : this.currentTab}`], {
                relativeTo: this.route,
                replaceUrl: true,
                queryParamsHandling: 'preserve',
            });
            this.webAnalyticModel.tab = this.currentTab;
            this.commonEventService.sendWAModel(this.webAnalyticModel);
        }
    }

    closeClick() {
        this.trackingService.submitTracking({
            LabelEvent: this.webAnalyticModel?.page,
            ActionEvent: 'Close CTA - Clicked',
            LocationEvent: this.webAnalyticModel?.tab,
            EventDetails: this.webAnalyticModel?.promoId,
            campaignId: this.webAnalyticModel?.campaignId,
            messageType: this.webAnalyticModel?.messageType,
        });
        this.goBack();
    }

    backClick() {
        this.trackingService.submitTracking({
            LabelEvent: this.webAnalyticModel?.page,
            ActionEvent: 'Back CTA - Clicked',
            LocationEvent: this.webAnalyticModel?.tab,
            EventDetails: this.webAnalyticModel?.promoId,
            campaignId: this.webAnalyticModel?.campaignId,
            messageType: this.webAnalyticModel?.messageType,
        });
        this.goBack();
    }

    goBack() {
        this.ccbEventService.sentCCBEventToMobileApp();
        this.commonService.goBack();
    }

    ngOnDestroy() {
        dispatchEvent(new CustomEvent('gs:showFloatingEntry'));
        this.destroy$.next();
        this.destroy$.complete();
    }
}
